import { MentorSession, MentorSessionStats, RawSessionData } from "@/types/mentorSessions";

export function formatSessions(
  sessionData: RawSessionData[], 
  playerProfilesMap: Map<string, { nickname: string; image_url: string | null }>
): MentorSession[] {
  console.log('🔄 [formatSessions] Iniciando formatação com:', {
    sessionsCount: sessionData.length,
    mapSize: playerProfilesMap.size,
    mapEntries: Array.from(playerProfilesMap.entries())
  });

  return sessionData
    .map(session => {
      console.log(`🔍 [formatSessions] Processando sessão ${session.id} para player ${session.player_profile_id}`);
      
      // Extract player info from the map we created
      const playerInfo = playerProfilesMap.get(session.player_profile_id);
      const playerName = playerInfo?.nickname || "Jogador";
      const playerImage = playerInfo?.image_url || null;
      
      console.log(`👤 [formatSessions] Player info para ${session.player_profile_id}:`, {
        found: !!playerInfo,
        nickname: playerName,
        image: playerImage
      });
      
      // Extract game name from nested data
      const gameName = session.games ? session.games.name : "Jogo não especificado";
      
      // Get payment information
      const paymentInfo = session.payments && session.payments.length > 0 ? session.payments[0] : null;
      
      // Considerar como pago: completed, succeeded, paid
      const isPaid = paymentInfo && (
        paymentInfo.status === "completed" || 
        paymentInfo.status === "succeeded" || 
        paymentInfo.status === "paid"
      );
      
      // CORREÇÃO: Obter o preço da mentorship_offering, não do pagamento
      // Isso garante que temos o valor original da mentoria
      let priceCents = 0;
      
      if (session.mentorship_offerings && session.mentorship_offerings.price_cents) {
        // Valor da offering (correto)
        priceCents = session.mentorship_offerings.price_cents;
      } else if (paymentInfo && paymentInfo.amount_cents) {
        // Fallback para valor do pagamento
        priceCents = paymentInfo.amount_cents;
      } else if (session.status === "confirmado" || session.status === "concluido") {
        // Fallback final - valor padrão para sessões confirmadas
        priceCents = 1600; // R$ 16,00 como exemplo
      }
      
      console.log(`💳 [formatSessions] Valor da sessão ${session.id}:`, {
        hasOffering: !!(session.mentorship_offerings),
        offeringPrice: session.mentorship_offerings?.price_cents,
        hasPayments: !!(session.payments && session.payments.length > 0),
        paymentAmount: paymentInfo?.amount_cents,
        paymentStatus: paymentInfo?.status,
        finalPrice: priceCents,
        isPaid,
        sessionStatus: session.status
      });
      
      // Get mentorship title and cover from the offering data
      const mentorshipTitle = session.mentorship_offerings?.title || `Mentoria de ${gameName}`;
      const mentorshipCoverUrl = session.mentorship_offerings?.cover_image_url || null;
      
      const formattedSession = {
        id: session.id,
        player_name: playerName,
        player_profile_id: session.player_profile_id,
        player_image_url: playerImage,
        game_name: gameName,
        scheduled_date: session.scheduled_date,
        scheduled_start: session.scheduled_start,
        scheduled_end: session.scheduled_end,
        status: session.status,
        price_cents: priceCents,
        is_paid: isPaid,
        meeting_link: session.meeting_link,
        mentorship_title: mentorshipTitle,
        mentorship_cover_url: mentorshipCoverUrl,
      };
      
      console.log(`✅ [formatSessions] Sessão formatada:`, formattedSession);
      return formattedSession;
    });
}

export function calculateStats(sessions: MentorSession[], commissionRate: number = 0.2): MentorSessionStats {
  console.log('📊 [calculateStats] Iniciando cálculo com:', {
    sessionsCount: sessions.length,
    commissionRate,
    sessions: sessions.map(s => ({
      id: s.id,
      price_cents: s.price_cents,
      is_paid: s.is_paid,
      status: s.status
    }))
  });

  // Sessões pagas (com pagamento confirmado) OU sessões confirmadas/concluídas
  const paidSessions = sessions.filter(session => 
    session.is_paid || 
    session.status === "confirmado" || 
    session.status === "concluido"
  );
  console.log('💰 [calculateStats] Sessões consideradas como vendidas:', paidSessions.length, paidSessions.map(s => ({ 
    id: s.id, 
    price_cents: s.price_cents, 
    is_paid: s.is_paid, 
    status: s.status 
  })));
  
  // Valor total bruto de todas as sessões pagas
  const totalGrossValue = paidSessions.reduce((sum, session) => sum + session.price_cents, 0);
  console.log('💵 [calculateStats] Valor bruto total:', totalGrossValue);
  
  // Valor líquido após comissão (mentor fica com 80% por padrão, ou conforme taxa personalizada)
  const mentorPercentage = 1 - commissionRate; // Se comissão é 20%, mentor fica com 80%
  const totalNetValue = Math.round(totalGrossValue * mentorPercentage);
  console.log('💎 [calculateStats] Valor líquido:', totalNetValue, 'com porcentagem do mentor:', mentorPercentage);
  
  // Sessões concluídas
  const completedSessions = sessions.filter(session => session.status === "concluido").length;
  
  // Sessões agendadas (confirmadas ou pendentes)
  const scheduledSessions = sessions.filter(session => 
    session.status === "confirmado" || session.status === "pendente"
  ).length;
  
  // Valor total de todas as sessões (incluindo não pagas) - manter para compatibilidade
  const totalValue = sessions.reduce((sum, session) => sum + session.price_cents, 0);
  
  const stats = {
    totalSessions: sessions.length,
    totalValue,
    pendingSessions: scheduledSessions, // Manter para compatibilidade
    // Novas métricas
    paidSessions: paidSessions.length,
    totalNetValue,
    completedSessions,
    scheduledSessions
  };
  
  console.log('📈 [calculateStats] Estatísticas finais:', stats);
  return stats;
}
